/* eslint-disable react/jsx-key */
import styles from './SingleAssetSelfTestOverview.module.scss';
import InsightsCard from '@customer/components/data-display/insights-card/InsightsCard';
import FaultLevelInsight from '@customer/components/data-display/fault-level-insight/FaultLevelInsight';
import {
  AssetSelfTestLevelSummaryModel,
  AssetSelfTestQueryParams,
  useGetAssetSelfTestSummaryQuery,
} from '@customer/store/apis/assetsApi';
import { useAppSelector } from '@customer/store/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import Skeleton from 'react-loading-skeleton';
import useFilters from '@customer/filters/useFilters';
import { FilterGroup } from '@customer/filters/types';

interface SingleAssetSelfTestOverviewProps {
  assetId?: string;
}

export const SingleAssetSelfTestOverview = ({ assetId }: SingleAssetSelfTestOverviewProps) => {
  const { t } = useTranslation();
  const timeRange = useAppSelector(state => state.global.timeRange);
  const { getTimeRangeText, showDurationFromSeconds } = useLocaleFormatter();

  const filters = useFilters<AssetSelfTestQueryParams>(FilterGroup.AssetSelfTest);

  // use data instead of currentData for smoother transition after query changes.
  const { data: assetSelfTestSummary } = useGetAssetSelfTestSummaryQuery(
    typeof assetId === 'undefined'
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            timeRange,
          },
        },
    { refetchOnMountOrArgChange: 60 },
  );

  const { ign, lvls = [] } = assetSelfTestSummary ?? {};

  // Remove punctuation from the human readable duration string: '12h, 30m, 15s.' => '12h 30m 15s'
  const compactDuration = showDurationFromSeconds(ign ?? 0).replace(/,|\./g, '');

  // Minimum 2 col display, even while loading. Round to guarantee integer precision.
  const numCols = Math.max(Math.round(1 + lvls.length), 2);

  const levelNodes = lvls.map((levelSummary: AssetSelfTestLevelSummaryModel) => {
    return (
      <div key={levelSummary.lvl}>
        <FaultLevelInsight
          faultLevel={levelSummary.lvl}
          timeRange={timeRange}
          totalFaults={levelSummary.tot}
          activeFaults={levelSummary.act}
          duration={levelSummary.dur}
          ign={ign}
        />
      </div>
    );
  });

  return (
    <InsightsCard
      cols={numCols}
      sections={[
        <div className={styles.ign}>
          <div className={styles.ign__label}>{t('SelfTestIgnitionOnTime')}</div>
          <div className={styles.ign__label}>{getTimeRangeText(timeRange, t)}</div>
          <div className={styles.ign__duration}>{compactDuration}</div>
        </div>,
        ...(lvls.length ? levelNodes : [<Skeleton width="100%" height="100%" />]),
      ]}
    />
  );
};

export default SingleAssetSelfTestOverview;
